import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { readLastTeam, rememberLastTeam } from "../utils/signOutStorage";

const TeamContext = createContext(undefined);

// A failed first load is retried this many times, this far apart (the
// profile's backoff), before it is reported. postgrest's own retries are off
// for that read so these alone set the timing. A refetch with teams already
// on screen keeps postgrest's retries: it has no loop of its own.
const TEAMS_RETRY_DELAYS_MS = [1000, 3000];
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const readTeams = async (userId, { postgrestRetries = false } = {}) => {
  try {
    return await supabase
      .from('teams')
      .select('id, name, conference_name, sport, gender, coach_id, org_type, org_type_detail, created_at')
      .eq('coach_id', userId)
      .retry(postgrestRetries)
      .order('created_at', { ascending: false });
  } catch (error) {
    return { data: null, error };
  }
};

// retryDelaysMs is for tests; the app always uses the profile's backoff.
export function TeamProvider({ children, retryDelaysMs = TEAMS_RETRY_DELAYS_MS }) {
  const [teams, setTeams] = useState([]);
  const [activeTeam, setActiveTeam] = useState(null);
  const [loadingTeams, setLoadingTeams] = useState(true);
  // The coach's teams could not be read and none are on screen. Without this
  // a failed load looked exactly like "no teams yet": the Team page sent a
  // coach with teams to Team Setup, where they could make a duplicate, and
  // other pages offered "Create team" (QA 2026-10-07).
  const [teamsError, setTeamsError] = useState(null);
  // Whose teams are loaded, so a switch can be remembered for that coach.
  const teamsUserIdRef = useRef(null);
  // How many of that coach's teams are on screen.
  const loadedCountRef = useRef(0);
  // Whose teams were asked for last (for a retry), and which load is current,
  // so a slow or retrying load can't land after a sign-out or a newer load.
  const requestedUserIdRef = useRef(null);
  const loadSeqRef = useRef(0);

  const clearTeams = useCallback(() => {
    loadSeqRef.current += 1;
    teamsUserIdRef.current = null;
    loadedCountRef.current = 0;
    requestedUserIdRef.current = null;
    setTeams([]);
    setActiveTeam(null);
    setTeamsError(null);
    setLoadingTeams(false);
  }, []);

  const fetchUserTeams = useCallback(async (userId) => {
    if (!userId) {
      clearTeams();
      return;
    }

    const seq = loadSeqRef.current + 1;
    loadSeqRef.current = seq;
    requestedUserIdRef.current = userId;
    // Only a load with nothing of this coach's on screen retries and reports
    // a failure; a failed refetch keeps the teams already shown. "Nothing on
    // screen" includes a coach whose last load found no team: the refetch
    // right after they create their first one must not fail into "no team"
    // (Team Setup again, and a duplicate team).
    const firstLoad = teamsUserIdRef.current !== userId || loadedCountRef.current === 0;
    setLoadingTeams(true);
    try {
      let { data, error } = await readTeams(userId, { postgrestRetries: !firstLoad });
      for (const delayMs of firstLoad ? retryDelaysMs : []) {
        if (!error || loadSeqRef.current !== seq) break;
        await wait(delayMs);
        if (loadSeqRef.current !== seq) break;
        ({ data, error } = await readTeams(userId));
      }
      if (loadSeqRef.current !== seq) return;

      if (error) throw error;

      const nextTeams = data || [];
      teamsUserIdRef.current = userId;
      loadedCountRef.current = nextTeams.length;
      setTeamsError(null);
      setTeams(nextTeams);
      // This tab's pointer first, then the team this coach last used (kept
      // across sign-out), then their newest team. Each only while the coach
      // still has that team.
      const savedActiveTeamId = localStorage.getItem('activeTeamId');
      const lastTeamId = readLastTeam(userId);
      const nextActiveTeam =
        nextTeams.find((team) => team.id === savedActiveTeamId) ||
        nextTeams.find((team) => team.id === lastTeamId) ||
        (nextTeams.length > 0 ? nextTeams[0] : null);

      setActiveTeam(nextActiveTeam);

      if (nextActiveTeam) {
        localStorage.setItem('activeTeamId', nextActiveTeam.id);
        rememberLastTeam(userId, nextActiveTeam.id);
      } else {
        localStorage.removeItem('activeTeamId');
      }
    } catch (error) {
      if (loadSeqRef.current !== seq) return;
      console.error('Error fetching user teams:', error?.message);
      // A failed refetch for the same coach keeps the teams on screen. With
      // nothing loaded for this coach, say the load failed; the ref stays
      // empty so the next good load replaces everything.
      if (firstLoad) {
        teamsUserIdRef.current = null;
        loadedCountRef.current = 0;
        setTeams([]);
        setActiveTeam(null);
        setTeamsError(error || new Error('Could not load teams.'));
      }
    } finally {
      if (loadSeqRef.current === seq) setLoadingTeams(false);
    }
  }, [clearTeams, retryDelaysMs]);

  const retryTeams = useCallback(async (userId) => {
    const targetUserId = userId || requestedUserIdRef.current;
    if (!targetUserId) return;
    await fetchUserTeams(targetUserId);
  }, [fetchUserTeams]);

  // A failed load heals on its own when the connection comes back or the
  // coach returns to the tab, as a reload would.
  useEffect(() => {
    if (!teamsError || typeof window === 'undefined') return undefined;
    const retryWhenVisible = () => {
      if (document.visibilityState === 'hidden') return;
      retryTeams();
    };
    window.addEventListener('online', retryWhenVisible);
    document.addEventListener('visibilitychange', retryWhenVisible);
    return () => {
      window.removeEventListener('online', retryWhenVisible);
      document.removeEventListener('visibilitychange', retryWhenVisible);
    };
  }, [teamsError, retryTeams]);

  const switchActiveTeam = useCallback((teamId) => {
    setActiveTeam((current) => {
      if (current?.id === teamId) return current;
      const nextTeam = teams.find((team) => team.id === teamId) || null;
      if (!nextTeam) {
        // Unknown/stale id: keep the current team instead of clearing it, and
        // drop the stale localStorage pointer so it can't resurface later.
        try {
          if (localStorage.getItem('activeTeamId') === teamId) localStorage.removeItem('activeTeamId');
        } catch { /* storage unavailable */ }
        return current;
      }
      localStorage.setItem('activeTeamId', teamId);
      rememberLastTeam(teamsUserIdRef.current, teamId);
      return nextTeam;
    });
  }, [teams]);

  const refreshTeams = useCallback(async (userId, preferTeamId) => {
    if (!userId) {
      clearTeams();
      return;
    }
    if (preferTeamId) localStorage.setItem('activeTeamId', preferTeamId);
    await fetchUserTeams(userId);
  }, [clearTeams, fetchUserTeams]);

  const value = useMemo(() => ({
    teams,
    activeTeam,
    loadingTeams,
    teamsError,
    switchActiveTeam,
    refreshTeams,
    retryTeams,
    fetchUserTeams,
    clearTeams,
  }), [teams, activeTeam, loadingTeams, teamsError, switchActiveTeam, refreshTeams, retryTeams, fetchUserTeams, clearTeams]);

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error("useTeam must be used within a TeamProvider");
  }
  return context;
};
